import Image from 'next/image';
import { ClockIcon } from '@phosphor-icons/react';
import Tag from '_components/UI/Tag';
import { DealTagStyle } from '@adapters/prismic/types/redesignTypes';

import styles from './DealCard.module.scss';

export interface DealCardProps {
  tagStyle?: DealTagStyle;
  tagText?: string;
  imageUrl?: string;
  imageAlt?: string;
  /** Discount copy split into display lines (e.g. ["Up to 40% off", "All Sandals"]). */
  copy?: string[];
  /**
   * Destination for the tile. Omit (or leave blank) to render a static,
   * non-interactive tile — used to promote deals that have no page yet.
   */
  href?: string;
  onClick?: () => void;
  priority?: boolean;
  /**
   * Reserve the fixed-height tag row even when this tile has no tag, so every
   * tile in a strip lines up. Set by the parent when at least one tile is
   * tagged; when no tile is tagged the row is dropped from all of them.
   */
  reserveTagSpace?: boolean;
}

/** A tile shows a tag only when it has both a tag style and non-blank text. */
export const dealCardHasTag = ({
  tagStyle,
  tagText,
}: Pick<DealCardProps, 'tagStyle' | 'tagText'>): boolean =>
  !!tagStyle && tagStyle !== DealTagStyle.none && !!tagText?.trim();

const DealCard = ({
  tagStyle = DealTagStyle.none,
  tagText,
  imageUrl,
  imageAlt = '',
  copy = [],
  href,
  onClick,
  priority = false,
  reserveTagSpace = false,
}: DealCardProps) => {
  const showTag = dealCardHasTag({ tagStyle, tagText });
  const showTagRow = showTag || reserveTagSpace;
  const destination = href?.trim();

  const content = (
    <>
      {showTagRow && (
        <div className={styles.tagSpacer}>
          {showTag && (
            <Tag onDarkBg size="small" className={styles.tag}>
              {tagStyle === DealTagStyle.dealTimer && (
                <ClockIcon
                  size={12}
                  weight="regular"
                  aria-hidden
                  className={styles.clockIcon}
                />
              )}
              {tagText}
            </Tag>
          )}
        </div>
      )}

      <div className={styles.imageWrapper}>
        {imageUrl && (
          <Image
            src={imageUrl}
            alt={imageAlt}
            fill
            sizes="(min-width: 988px) 198px, 128px"
            className={styles.image}
            priority={priority}
          />
        )}
      </div>

      {copy.length > 0 && (
        <span className={styles.copy}>
          {copy.map((line, i) => (
            <span key={`${line}-${i}`} className={styles.copyLine}>
              {line}
            </span>
          ))}
        </span>
      )}
    </>
  );

  return (
    <article
      className={`${styles.card}${destination ? ` ${styles.interactive}` : ''}`}
    >
      {destination ? (
        <a className={styles.link} href={destination} onClick={onClick}>
          {content}
        </a>
      ) : (
        <div className={styles.link}>{content}</div>
      )}
    </article>
  );
};

export default DealCard;
